'use client';

import { useEffect, useRef, useState } from 'react';
import { Howl } from 'howler';

interface AudioPlayerProps {
    src: string;
    title?: string;
    mimeType?: string;
    className?: string;
    onPlay?: () => void;
    onPause?: () => void;
    onEnd?: () => void;
}

export function AudioPlayer({ 
    src, 
    title, 
    mimeType, 
    className = '',
    onPlay,
    onPause,
    onEnd
}: AudioPlayerProps) {
    const [isPlaying, setIsPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const soundRef = useRef<Howl | null>(null);
    const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // Initialize Howler
        const sound = new Howl({
            src: [src],
            html5: true,
            format: mimeType ? [mimeType.split('/')[1]] : undefined,
            onload: () => {
                setDuration(sound.duration());
                setIsLoading(false);
            },
            onplay: () => {
                setIsPlaying(true);
                onPlay?.();
                // Update progress
                progressIntervalRef.current = setInterval(() => {
                    setCurrentTime(sound.seek());
                }, 100);
            },
            onpause: () => {
                setIsPlaying(false);
                onPause?.();
                if (progressIntervalRef.current) {
                    clearInterval(progressIntervalRef.current);
                }
            },
            onstop: () => {
                setIsPlaying(false);
                setCurrentTime(0);
                if (progressIntervalRef.current) {
                    clearInterval(progressIntervalRef.current);
                }
            },
            onend: () => {
                setIsPlaying(false);
                setCurrentTime(0);
                onEnd?.();
                if (progressIntervalRef.current) {
                    clearInterval(progressIntervalRef.current);
                }
            },
            onloaderror: () => {
                setIsLoading(false);
            },
            onplayerror: () => {
                setIsLoading(false);
            }
        });

        soundRef.current = sound;
        setIsLoading(true);

        return () => {
            sound.unload();
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
            }
        };
    }, [src, mimeType, onPlay, onPause, onEnd]);

    const togglePlay = () => {
        if (!soundRef.current) return;

        if (isPlaying) {
            soundRef.current.pause();
        } else {
            soundRef.current.play();
        }
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!soundRef.current) return;
        const seekTime = parseFloat(e.target.value);
        soundRef.current.seek(seekTime);
        setCurrentTime(seekTime);
    };

    const formatTime = (seconds: number): string => {
        if (isNaN(seconds) || seconds === 0) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className={`flex items-center gap-2 bg-gray-100 rounded-lg p-2 ${className}`}>
            {/* Play/Pause Button */}
            <button
                onClick={togglePlay}
                disabled={isLoading}
                className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-primary-500 hover:bg-primary-600 disabled:bg-gray-400 text-white rounded-full transition-colors"
                aria-label={isPlaying ? 'Pause' : 'Play'}
            >
                {isLoading ? (
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                ) : isPlaying ? (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                    </svg>
                ) : (
                    <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                    </svg>
                )}
            </button>

            {/* Progress Bar */}
            <div className="flex-1 flex items-center gap-2">
                <span className="text-xs text-gray-600 font-mono w-10 text-right">
                    {formatTime(currentTime)}
                </span>
                <input
                    type="range"
                    min="0"
                    max={duration || 0}
                    step="0.1"
                    value={currentTime}
                    onChange={handleSeek}
                    disabled={isLoading || duration === 0}
                    className="flex-1 h-1 bg-gray-300 rounded-lg appearance-none cursor-pointer disabled:cursor-not-allowed [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary-500 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary-500 [&::-moz-range-thumb]:border-0"
                />
                <span className="text-xs text-gray-600 font-mono w-10">
                    {formatTime(duration)}
                </span>
            </div>
        </div>
    );
}
